
import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSupabaseAuth } from '@/hooks/auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SidebarTrigger } from '@/components/ui/sidebar';
import NotificationBell from '@/components/notifications/NotificationBell';
import GlobalSearch from '@/components/navigation/GlobalSearch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Bell, 
  Settings, 
  User, 
  LogOut, 
  Moon, 
  Sun,
  Shield,
  ArrowLeft,
  Users
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { useRealProgressData } from '@/hooks/useRealProgressData';

const ModernHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { courseId, moduleId } = useParams();
  const { user, profile, signOut, hasAccess } = useSupabaseAuth();
  const { theme, setTheme } = useTheme();

  // Check if we're on a SQL module page
  const isSQLModulePage = location.pathname.includes('/sql-courses/') && moduleId;
  
  // Get progress data for SQL module pages
  const progressData = useRealProgressData(moduleId || "", courseId || "");

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const getUserInitials = () => {
    if (profile?.name) {
      return profile.name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    if (user?.user_metadata?.name) {
      return user.user_metadata.name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <header className="sticky top-0 z-40 w-full h-16 border-b bg-background">
      <div className="container flex h-full items-center space-x-4 sm:justify-between sm:space-x-0">
        <div className="flex items-center space-x-4">
          <SidebarTrigger />
          
          {/* SQL Course Navigation - only show on SQL module pages */}
          {isSQLModulePage && courseId && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/sql-courses/${courseId}`)}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Back to Course
            </Button>
          )}
        </div>

        <div className="flex items-center space-x-4">
          {/* Global Search */}
          <GlobalSearch />

          {/* SQL Course Progress - only show on SQL module pages */}
          {isSQLModulePage && !progressData.isLoading && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground hidden sm:inline-block">Course Progress</span>
              <div className="w-32 bg-secondary/30 rounded-full overflow-hidden">
                <div 
                  className="h-2 bg-primary transition-all duration-300" 
                  style={{ width: `${progressData.courseProgress}%` }}
                />
              </div>
              <span className="text-xs font-medium">{progressData.courseProgress}%</span>
            </div>
          )}

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            className="h-9 w-9 p-0"
          >
            <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          </Button>

          {/* Notifications */}
          <NotificationBell />

          {/* Staff Portal Badge */}
          {hasAccess('staff') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/staff')}
              className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-300"
            >
              <Users className="h-4 w-4 mr-2" />
              Staff Portal
            </Button>
          )}

          {/* Admin Badge */}
          {hasAccess('admin') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin')}
              className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100 dark:bg-red-950 dark:border-red-800 dark:text-red-300"
            >
              <Shield className="h-4 w-4 mr-2" />
              Admin
            </Button>
          )}

          {/* User Menu */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full p-0">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={profile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-semibold">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 z-50" align="end">
                <div className="flex items-center justify-start gap-2 p-2">
                  <div className="flex flex-col space-y-1 leading-none">
                     <p className="font-medium">
                       {profile?.name || 
                        user?.user_metadata?.name || 
                        user?.email?.split('@')[0] || 
                        'User'}
                     </p>
                     <p className="text-xs text-muted-foreground">
                       {profile?.email || user?.email || 'No email'}
                     </p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex space-x-2">
              <Button onClick={() => navigate('/login')}>
                Sign In
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default ModernHeader;
